import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import TransactionHistoryRefreshButton from "../../components/TransactionHistoryRefreshButton";
import { errorMessage } from "../../utils/errorMessage";
import {
  getActivityTypeConfig,
  withDefaultSubtype,
  type ActivityFilter,
} from "../../utils/activity/filters";
import {
  useRefreshTransactions,
  useTransactions,
} from "../../hooks/queries/useTransactions";
import {
  ACTIVITY_COIN_PAGE_LIMIT,
  ACTIVITY_POLL_INTERVAL,
} from "../../hooks/queries/activityKeys";
import ActivityTypeTabBar from "../activity/ActivityTypeTabBar";
import ActivityList from "../activity/ActivityList";
import ActivityFilterPanel, {
  DEFAULT_SHEET_FILTERS,
  type ActivitySheetFilters,
} from "../activity/ActivityFilterPanel";
import { MdFilterList } from "react-icons/md";

/**
 * Single-coin activity list.
 *
 * Mirrors the app: crypto *subtypes* are the tabs here (the type is always
 * crypto), so the filter panel is rendered without a `type` and shows only
 * status and date — the Category section would duplicate the tab bar.
 */
const SUBTYPE_ROUTES = (getActivityTypeConfig("crypto")?.subtypes ?? []).map(
  (s) => ({ key: s.key, label: s.label })
);

const RecentTnxn = ({ selectedCoinObj, setSelectedTransaction }: any) => {
  const [index, setIndex] = useState(0);
  const [filterOpen, setFilterOpen] = useState(false);
  const [sheetFilters, setSheetFilters] =
    useState<ActivitySheetFilters>(DEFAULT_SHEET_FILTERS);

  const coin = selectedCoinObj?.abbr?.toLowerCase();
  const subtype = SUBTYPE_ROUTES[index]?.key;

  const filter: ActivityFilter = useMemo(
    () => ({
      ...withDefaultSubtype("crypto", subtype),
      status: sheetFilters.status,
      startDate: sheetFilters.startDate,
      endDate: sheetFilters.endDate,
      coin,
    }),
    [subtype, sheetFilters, coin]
  );

  const {
    data,
    isPending,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isError,
    error,
  } = useTransactions(filter, {
    limit: ACTIVITY_COIN_PAGE_LIMIT,
    enabled: Boolean(coin),
    refetchInterval: ACTIVITY_POLL_INTERVAL,
  });

  const refreshTransactions = useRefreshTransactions(
    filter,
    ACTIVITY_COIN_PAGE_LIMIT
  );
  const [isRefreshing, setIsRefreshing] = useState(false);

  const onRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await refreshTransactions();
    } finally {
      setIsRefreshing(false);
    }
  }, [refreshTransactions]);

  const onEndReached = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  useEffect(() => {
    if (isError) {
      toast.error(
        errorMessage((error as any)?.message || (error as any)?.data?.message)
      );
    }
  }, [isError, error]);

  const activeFilterCount =
    (sheetFilters.status ? 1 : 0) + (sheetFilters.datePreset !== "all" ? 1 : 0);

  return (
    <div className="w-full font-manrope mb-8 bg-white rounded-xl mt-8 dark:bg-[#1C2227] h-[500px] overflow-auto">
      <div className="w-full flex justify-between items-center sticky top-0 p-4 bg-gray-300 dark:bg-[#1F2C36]">
        <h3 className="font-bold text-paragraph text-[15px] dark:text-white">
          Recent Transactions
        </h3>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setFilterOpen(true)}
            className="relative flex items-center gap-1 px-2 py-1 rounded-lg text-gray-600 dark:text-gray-300 text-[13px] font-medium"
          >
            <MdFilterList className="text-[16px]" />
            Filter
            {activeFilterCount > 0 && (
              <span className="absolute -top-1 -right-1 w-[15px] h-[15px] rounded-full bg-secondary_button text-white text-[9px] font-bold flex justify-center items-center">
                {activeFilterCount}
              </span>
            )}
          </button>
          <TransactionHistoryRefreshButton
            onRefresh={onRefresh}
            isRefreshing={isRefreshing}
          />
          <Link
            to="/activity"
            className="text-[13px] transition ease-out duration-300 text-secondary_button flex justify-center items-center"
          >
            See all
          </Link>
        </div>
      </div>

      <div className="px-3">
        <ActivityTypeTabBar
          routes={SUBTYPE_ROUTES}
          index={index}
          onChange={setIndex}
        />
        <ActivityList
          items={data?.items ?? []}
          isLoading={isPending}
          isFetchingNextPage={isFetchingNextPage}
          hasNextPage={hasNextPage}
          onEndReached={onEndReached}
          onSelect={setSelectedTransaction}
          dense
        />
      </div>

      {/* No `type` prop: subtype is the tab bar here, so the panel stays
          status/date only. */}
      <ActivityFilterPanel
        open={filterOpen}
        value={sheetFilters}
        onApply={(filters) => {
          setSheetFilters(filters);
          setFilterOpen(false);
        }}
        onClose={() => setFilterOpen(false)}
      />
    </div>
  );
};

export default RecentTnxn;
